import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { sky } from './skyState';
import { QUALITY } from './quality';

// ── THE TOWN'S LIGHT RIG ─────────────────────────────────────────────────────
//
// Replaces the bare <ambientLight> + <directionalLight> in SceneContents. Same
// mounting rule as before (the caller still withholds it in plan view and in
// sealed mesh rooms), and within a mount the light COUNT never changes: three
// compiles the number of each light type into every lit shader, so mounting or
// unmounting one mid-walk recompiles the whole city. Modes change VALUES only.
//
//   sun    the street under the new look: a sun that follows the clock (moon
//          after dark), a sky/ground hemisphere fill instead of flat ambient, and
//          a shadow frustum that FOLLOWS THE PLAYER — tight enough that the
//          shadows are crisp at your feet, travelling with you so every street
//          you walk down has them.
//   classic the old rig's numbers exactly (tod.ambIntensity, tod.dirIntensity,
//          tod.keyPos, three's default ±5 shadow box) — interiors that keep the
//          outdoor rig were tuned under these, so they get these.
//
// The hemisphere light sits at 0 in classic mode rather than being absent, for
// the same light-count reason.

const UP = new THREE.Vector3(0, 1, 0);

export default function WorldLight({ tod, mode = 'classic' }) {
  const camera = useThree((s) => s.camera);
  const scene = useThree((s) => s.scene);
  const dir = useRef();
  const hemi = useRef();
  const amb = useRef();
  const target = useMemo(() => new THREE.Object3D(), []);
  // The target has to be in the graph for its world matrix to update.
  useEffect(() => { scene.add(target); return () => { scene.remove(target); }; }, [scene, target]);

  const reach = QUALITY.shadowReach || 50;
  const size = QUALITY.shadowSize || 2048;

  // Shadow camera shape per mode. Done in an effect, not every frame: changing
  // the projection is cheap but there is no reason to repeat it.
  useEffect(() => {
    const l = dir.current;
    if (!l) return;
    const cam = l.shadow.camera;
    if (mode === 'sun') {
      cam.left = -reach; cam.right = reach; cam.top = reach; cam.bottom = -reach;
      cam.near = 1; cam.far = 420;
      l.shadow.bias = -0.00035;
      l.shadow.normalBias = 0.035;
      l.shadow.radius = 3;
    } else {
      // three's DirectionalLightShadow defaults — what the classic rig had.
      cam.left = -5; cam.right = 5; cam.top = 5; cam.bottom = -5;
      cam.near = 0.5; cam.far = 500;
      l.shadow.bias = 0; l.shadow.normalBias = 0; l.shadow.radius = 1;
    }
    cam.updateProjectionMatrix();
    l.target = target;
  }, [mode, reach, target]);

  // ── EVERYTHING ON THE STREET TAKES PART IN THE SUN ─────────────────────────
  //
  // Not just tidiness: it is what makes the city's shadows show up at all.
  // three caches `receiveShadow` per MATERIAL but sets it as a uniform per
  // PROGRAM, and the city's materials are shared by two kinds of mesh — the
  // batches (one program variant) and the building-brush / venue-shell clones
  // placed outside the model (another). When those disagree about
  // receiveShadow, the cache and the batched program's uniform fall out of step
  // and the whole city silently stops receiving (bisected 2026-10-11: forcing
  // the flag on every mesh brought every shadow back). Making the street
  // consistent is both the fix and the look: residents, cars and placed
  // buildings sit in the same light as everything else.
  //
  // A sweep rather than a one-off, because residents and placements stream in
  // as you walk. Each object is looked at once.
  const seen = useMemo(() => new WeakSet(), []);
  const sweepClock = useRef(0);
  const sweep = () => {
    scene.traverse((o) => {
      if (seen.has(o)) return;
      seen.add(o);
      if (!o.isMesh || o.userData?.noShadow) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      if (!mats.length || !mats[0]) return;
      // Only lit, opaque, depth-writing surfaces. Glass, decals, labels, glows
      // and the sky neither cast nor usefully receive.
      const solid = mats.every((m) => m && !m.transparent && m.depthWrite !== false && m.colorWrite !== false);
      const lit = mats.some((m) => m && (m.isMeshStandardMaterial || m.isMeshPhongMaterial || m.isMeshLambertMaterial || m.isMeshToonMaterial));
      if (!solid || !lit) return;
      o.receiveShadow = true;
      // Flat ground only shadows itself; everything else casts.
      if (!o.userData?.cityBatch || o.material?.name !== 'RoadsGround') o.castShadow = true;
    });
  };

  const fwd = useMemo(() => new THREE.Vector3(), []);
  const centre = useMemo(() => new THREE.Vector3(), []);
  const lightSpace = useMemo(() => new THREE.Matrix4(), []);
  const tmp = useMemo(() => new THREE.Vector3(), []);

  useFrame((_, dt) => {
    const l = dir.current; const h = hemi.current; const a = amb.current;
    if (!l || !h || !a) return;
    if (mode === 'sun' && QUALITY.shadowSize) {
      sweepClock.current -= dt;
      if (sweepClock.current <= 0) { sweepClock.current = 1.5; sweep(); }
    }
    if (mode !== 'sun') {
      a.intensity = tod.ambIntensity; a.color.set(tod.ambColor);
      h.intensity = 0;
      l.intensity = tod.dirIntensity; l.color.set(tod.sunColor);
      l.position.set(tod.keyPos[0], tod.keyPos[1], tod.keyPos[2]);
      target.position.set(0, 0, 0);
      target.updateMatrixWorld();
      return;
    }

    a.intensity = sky.ambI; a.color.copy(sky.ambColor);
    h.intensity = sky.hemiI; h.color.copy(sky.hemiSky); h.groundColor.copy(sky.hemiGround);
    l.intensity = sky.lightI; l.color.copy(sky.lightColor);

    // FOLLOW THE PLAYER. Centre the box a little ahead of the camera — that is
    // where the eye is looking, and where a shadow's edge would be noticed.
    camera.getWorldDirection(fwd); fwd.y = 0;
    if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
    fwd.normalize();
    centre.copy(camera.position).addScaledVector(fwd, reach * 0.45);
    centre.y = 0;

    // SNAP TO THE SHADOW MAP'S TEXEL GRID, in light space. A box that slides
    // continuously re-rasterises every edge at a sub-texel offset each frame,
    // which reads as shadows crawling and shimmering as you walk. Moving it in
    // whole texels keeps each edge on the same texels until it actually moves.
    const texel = (2 * reach) / size;
    lightSpace.lookAt(tmp.set(0, 0, 0), tmp.copy(sky.lightDir).negate(), UP);
    const inv = lightSpace.clone().invert();
    tmp.copy(centre).applyMatrix4(inv);
    tmp.x = Math.round(tmp.x / texel) * texel;
    tmp.y = Math.round(tmp.y / texel) * texel;
    tmp.applyMatrix4(lightSpace);
    centre.copy(tmp);

    target.position.copy(centre);
    target.updateMatrixWorld();
    l.position.copy(centre).addScaledVector(sky.lightDir, 200);
  });

  return (
    <>
      <ambientLight ref={amb} intensity={tod.ambIntensity} color={tod.ambColor} />
      <hemisphereLight ref={hemi} args={['#ffffff', '#000000', 0]} />
      <directionalLight
        ref={dir}
        position={tod.keyPos}
        intensity={tod.dirIntensity}
        color={tod.sunColor}
        castShadow
        shadow-mapSize-width={size}
        shadow-mapSize-height={size}
      />
    </>
  );
}
